import React, { useEffect, useState } from "react";
import orderService from "../../services/order.service";
import { useNavigate } from "react-router-dom";

// ============================================================================
// 🔢 HELPER: Format Order ID (e.g., 39 -> OD0039)
// ============================================================================
const formatOrderId = (id) => {
  if (!id) return "";
  return "OD" + String(id).padStart(4, "0");
};

// ============================================================================
// ⏱️ TIMER COMPONENT: Handles the 3-minute countdown efficiently
// ============================================================================
const PaymentTimer = ({ createdAt, onExpire }) => {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const expireTime = new Date(createdAt).getTime() + 3 * 60 * 1000;
      return Math.max(0, expireTime - Date.now());
    };

    const initialTime = calculateTimeLeft();
    setTimeLeft(initialTime);

    if (initialTime === 0) {
      onExpire();
      return;
    }

    const interval = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      if (remaining === 0) {
        clearInterval(interval);
        onExpire();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [createdAt, onExpire]);

  if (timeLeft <= 0) return null;

  const minutes = Math.floor(timeLeft / 1000 / 60);
  const seconds = Math.floor((timeLeft / 1000) % 60);

  return (
    <span
      style={{
        color: "#d97706",
        fontWeight: "600",
        fontSize: "0.85rem",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        background: "#fffbeb",
        padding: "4px 8px",
        borderRadius: "4px",
        border: "1px solid #fde68a",
      }}
    >
      ⏳ Expires in {minutes}:{seconds.toString().padStart(2, "0")}
    </span>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const AccountOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [expiredIds, setExpiredIds] = useState(new Set());

  const navigate = useNavigate();

  const fetchOrders = async () => {
    try {
      const data = await orderService.getMyOrders();
      setOrders(data);

      // ✅ BACKGROUND CLEANUP: Check for orders that expired while the user was offline
      // This tells the DB to cancel them and free up inventory silently.
      data.forEach((order) => {
        if (
          order.status === "Pending Payment" ||
          order.status === "Payment Failed"
        ) {
          if (
            Date.now() - new Date(order.createdAt).getTime() >=
            3 * 60 * 1000
          ) {
            orderService
              .cancelOrder(
                order.order_id,
                "Auto-cancelled: Payment window expired (3 mins)",
              )
              .catch(() => console.log(`Cleanup skip for #${order.order_id}`));
          }
        }
      });
    } catch (error) {
      console.error("Failed to load orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line
  }, []);

  const getStatusClass = (status) => {
    if (!status) return "pending";
    const s = status.toLowerCase();
    if (s.includes("failed") || s.includes("cancel")) return "failed";
    if (s.includes("delivered") || s.includes("paid")) return "success";
    if (s.includes("shipped")) return "shipped";
    if (s.includes("processing")) return "processing";
    return "pending";
  };

  // ✅ DYNAMIC EXPIRATION LOGIC
  const handleExpire = async (orderId) => {
    // 1. Visually update UI instantly
    setExpiredIds((prev) => new Set(prev).add(orderId));

    // 2. Tell the DB to cancel it instantly
    try {
      await orderService.cancelOrder(
        orderId,
        "Payment timeout (3 minutes expired)",
      );
      fetchOrders(); // Sync with backend just to be safe
    } catch (err) {
      console.error("Auto-cancel failed:", err);
    }
  };

  const handleRetryPayment = async (e, orderId) => {
    e.stopPropagation();

    try {
      setIsProcessing(true);
      const sessionData = await orderService.retryPaymentSession(orderId);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: sessionData.amount,
        currency: "INR",
        name: "Zynventics Technologies",
        description: `Complete Payment for Order #${formatOrderId(orderId)}`,
        order_id: sessionData.razorpayOrderId,
        prefill: {
          name: sessionData.shipping_name,
          contact: sessionData.shipping_phone,
        },
        handler: async (response) => {
          try {
            await orderService.confirmRetryPayment({
              orderId: sessionData.orderId,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            alert("Payment successful!");
            fetchOrders();
          } catch (err) {
            console.error("Confirmation failed", err);
            alert("Payment completed but confirmation failed.");
          }
        },
        modal: {
          ondismiss: () => {
            console.log("User closed the retry modal.");
          },
        },
        theme: { color: "#4f46e5" },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        alert("Payment failed: " + response.error.description);
      });
      rzp.open();
    } catch (error) {
      console.error("Retry failed:", error);
      if (error.response?.data?.isExpired) {
        alert(
          "This order has expired (exceeded 3 minutes) and is now cancelled.",
        );
        fetchOrders();
      } else {
        alert(error.response?.data?.message || "Could not start payment.");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="account-section skeleton-loader">
        <p style={{ padding: "20px" }}>Loading your orders...</p>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="account-section fade-in">
        <div className="section-header">
          <h3>Order History</h3>
          <p>No orders found. Go shop some Zynventics batteries! 🔋</p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-section fade-in">
      <div className="section-header">
        <h3>Order History</h3>
        <p>Track, return, or view details for all your past orders.</p>
      </div>

      <div className="order-list">
        {orders.map((order) => {
          // Time calculation
          const orderAgeMs = Date.now() - new Date(order.createdAt).getTime();
          const isTimeExpired = orderAgeMs >= 3 * 60 * 1000;
          const isExpired = isTimeExpired || expiredIds.has(order.order_id);

          // ✅ VISUAL OVERRIDE: If the order was pending/failed but time is up, force it to show as Cancelled
          let displayStatus = order.status;
          let showExpiredWarning = false;

          if (
            (order.status === "Pending Payment" ||
              order.status === "Payment Failed") &&
            isExpired
          ) {
            displayStatus = "Cancelled";
            showExpiredWarning = true;
          }

          const statusClass = getStatusClass(displayStatus);

          // Only show payment controls if it hasn't expired yet
          const canPayNow =
            (order.status === "Pending Payment" ||
              order.status === "Payment Failed") &&
            !isExpired;

          return (
            <div
              key={order.order_id}
              className={`order-item-card ${canPayNow ? "needs-payment" : ""}`}
              onClick={() => navigate(`/account/orders/${order.order_id}`)}
              style={{ cursor: "pointer" }}
              title="Click to view order details"
            >
              <div className="order-item-header">
                {/* ✅ Format applied here */}
                <strong>Order #{formatOrderId(order.order_id)}</strong>
                {/* 🔴 Now uses displayStatus so it automatically turns red/Cancelled */}
                <span className={`status-badge ${statusClass}`}>
                  {displayStatus}
                </span>
              </div>

              <div className="order-item-body">
                <span>
                  Date: {new Date(order.createdAt).toLocaleDateString()}
                </span>
                <span className="order-total">
                  Total: ₹
                  {parseFloat(order.total_amount).toLocaleString("en-IN")}
                </span>
              </div>

              <div className="order-item-footer">
                {order.OrderItems && (
                  <p className="order-meta" style={{ margin: 0 }}>
                    {order.OrderItems.length}{" "}
                    {order.OrderItems.length === 1 ? "Item" : "Items"} included
                  </p>
                )}

                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  {canPayNow && (
                    <>
                      <PaymentTimer
                        createdAt={order.createdAt}
                        onExpire={() => handleExpire(order.order_id)}
                      />
                      <button
                        className="btn-retry-payment"
                        onClick={(e) => handleRetryPayment(e, order.order_id)}
                        disabled={isProcessing}
                      >
                        {isProcessing ? "Loading..." : "Pay Now ➔"}
                      </button>
                    </>
                  )}

                  {/* 🔴 Shows exactly why it was cancelled */}
                  {showExpiredWarning && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontWeight: "600",
                        fontSize: "0.85rem",
                      }}
                    >
                      Payment Window Expired
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AccountOrders;
